import { useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  Loader2,
  Pencil,
  Check,
  X,
  IdCard,
  Mail,
  Phone,
  CalendarDays,
  UserMinus,
  History,
  AlertTriangle,
  Paperclip,
  UserCheck,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { openProtectedFile } from "@/components/employee/tabHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { NumericInput } from "@/components/ui/numeric-input";
import { formatHuf } from "@/components/SalaryFormSheet";

// Százalék formázó — egész vagy max 2 tizedessel.
const formatPct = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const fmt = n % 1 === 0
    ? n.toLocaleString("hu-HU")
    : n.toLocaleString("hu-HU", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return `${fmt}%`;
};

const EMPLOYMENT_TYPE_OPTIONS = [
  { v: "efo", label: "EFO" },
  { v: "student_coop", label: "Diákszövetkezet" },
  { v: "full_time", label: "Teljes munkaidős" },
  { v: "part_time", label: "Rész munkaidős" },
];
const POSITION_OPTIONS = [
  { v: "staff", label: "Staff" },
  { v: "shift_leader", label: "Shift Leader" },
  { v: "manager", label: "Manager" },
  { v: "area_manager", label: "Area Manager" },
];
const EMPLOYMENT_MODE_OPTIONS = [
  { v: "employee", label: "Alkalmazotti" },
  { v: "partnership", label: "Társas Vállalkozó" },
  { v: "mandate", label: "Megbízási" },
  { v: "entrepreneur", label: "Vállalkozói" },
  { v: "efo", label: "EFO" },
  { v: "student_coop", label: "Diákszövetkezet" },
];
const SCHEDULE_OPTIONS = [
  { v: "five_day", label: "5 munkanapos" },
  { v: "variable", label: "Változó" },
];
const WEEKLY_HOURS_OPTIONS = [
  { v: 20, label: "20 óra / hét" },
  { v: 30, label: "30 óra / hét" },
  { v: 40, label: "40 óra / hét" },
];

const labelOf = (opts, v) => opts.find((o) => o.v === v)?.label || "—";
const formatDateHu = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("hu-HU", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

const EMP_FIELDS = [
  "employment_type",
  "employment_start_date",
  "probation_end_date",
  "company_id",
  "site_id",
  "gross_hourly_rate",
  "gross_monthly_salary",
  "gross_monthly_salary_words",
  "gross_hourly_rate_words",
  "position",
  "feor_number",
  "employment_mode",
  "schedule_type",
  "weekly_hours",
];

export function EmploymentDetailsSection({ employee, onEmployeeChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Commission approval
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeValue, setProposeValue] = useState("");
  const [approving, setApproving] = useState(false);

  // Termination
  const [terminationOpen, setTerminationOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/sites")])
      .then(([c, s]) => {
        setCompanies(c.data);
        setSites(s.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editing) return;
    const next = {};
    EMP_FIELDS.forEach((f) => {
      next[f] = employee[f] ?? "";
    });
    setForm(next);
    setErr("");
  }, [editing, employee]);

  const companyName = useMemo(
    () =>
      companies.find((c) => c._id === employee.company_id)?.name || null,
    [companies, employee.company_id]
  );
  const siteName = useMemo(
    () => sites.find((s) => s._id === employee.site_id)?.name || null,
    [sites, employee.site_id]
  );

  const visibleSites = useMemo(() => {
    if (!form.company_id) return sites;
    return sites.filter((s) => s.company_id === form.company_id);
  }, [sites, form.company_id]);

  const onSave = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = {};
      EMP_FIELDS.forEach((f) => {
        const v = form[f];
        payload[f] =
          v === "" || v === undefined
            ? null
            : ["gross_hourly_rate", "gross_monthly_salary"].includes(f)
            ? Number(v)
            : f === "weekly_hours"
            ? Number(v)
            : v;
      });
      const { data } = await api.patch(`/employees/${employee._id}`, payload);
      onEmployeeChange(data);
      toast.success("Munkaviszony adatok frissítve.");
      setEditing(false);
    } catch (e) {
      setErr(
        formatApiError(e?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  // Commission propose / approve / reject
  const submitCommissionPropose = async () => {
    const val = Number(proposeValue);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Érvénytelen összeg.");
      return;
    }
    try {
      const { data } = await api.post(
        `/employees/${employee._id}/default-commission/propose`,
        { new_value: val }
      );
      onEmployeeChange(data);
      toast.success("Jutalék módosítás jóváhagyásra vár.");
      setProposeOpen(false);
      setProposeValue("");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Mentés sikertelen."
      );
    }
  };

  const approveCommission = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(
        `/employees/${employee._id}/default-commission/approve`
      );
      onEmployeeChange(data);
      toast.success("Új jutalék jóváhagyva.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült jóváhagyni."
      );
    } finally {
      setApproving(false);
    }
  };

  const rejectCommission = async () => {
    if (!window.confirm("Elutasítod a függőben lévő jutalék módosítást?"))
      return;
    try {
      const { data } = await api.post(
        `/employees/${employee._id}/default-commission/reject`
      );
      onEmployeeChange(data);
      toast.success("Módosítás elutasítva.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült elutasítani."
      );
    }
  };

  const pendingValue = employee.default_gross_commission_pending;
  const currentValue = employee.default_gross_commission;

  return (
    <section
      data-testid="employment-details-section"
      className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
    >
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Briefcase className="h-3.5 w-3.5" strokeWidth={2} />
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Munkaviszony adatok
          </p>
        </div>
        {!editing ? (
          <div className="flex gap-2">
            {employee.employment_type && employee.status !== "inactive" && (
              <Button
                size="sm"
                variant="outline"
                data-testid="employment-terminate"
                onClick={() => setTerminationOpen(true)}
                className="text-rose-700 hover:text-rose-700 dark:text-rose-300"
              >
                <UserMinus className="h-3.5 w-3.5" />
                {employee.status === "notice" ? "Felmondás kezelése" : "Felmondás"}
              </Button>
            )}
            {employee.employment_type &&
              employee.status !== "inactive" &&
              employee.status !== "notice" && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="employment-edit"
                  onClick={() => setEditing(true)}
                >
                  <Pencil className="h-3.5 w-3.5" /> Szerkesztés
                </Button>
              )}
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(false)}
            >
              Mégse
            </Button>
            <Button size="sm" disabled={saving} data-testid="employment-save" onClick={onSave}>
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Mentés…
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" /> Mentés
                </>
              )}
            </Button>
          </div>
        )}
      </header>

      <div className="p-5">
        {/* Empty state: no active employment */}
        {!employee.employment_type && !employee.current_termination && (
          <div
            data-testid="no-employment-state"
            className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
          >
            <div className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
              <Briefcase className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <p className="text-sm font-medium">Nincs aktív munkaviszony</p>
            <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
              A munkavállalóhoz nincs rögzítve aktív munkaviszony. Új
              munkaviszony csak <b>onboarding</b> során, a Toborzás
              jelöltlistából indítható.
            </p>
          </div>
        )}

        {/* Notice period banner */}
        {employee.current_termination && (
          <NoticeBanner
            employee={employee}
            companyName={companyName}
            onEmployeeChange={onEmployeeChange}
          />
        )}

        {/* Mini employee summary + employment details (only if active employment exists) */}
        {employee.employment_type && (
          <>
            {/* Mini employee summary */}
            <div className="mb-5 flex flex-wrap gap-x-6 gap-y-1.5 rounded-md bg-muted/30 px-4 py-3 text-xs">
              <Mini icon={IdCard}>
                {employee.last_name} {employee.first_name}
              </Mini>
              {employee.birth_date && (
                <Mini icon={CalendarDays}>{formatDateHu(employee.birth_date)}</Mini>
              )}
              {employee.email && <Mini icon={Mail}>{employee.email}</Mini>}
              {employee.phone && <Mini icon={Phone}>{employee.phone}</Mini>}
            </div>
          </>
        )}

        {/* Pending commission notice */}
        {employee.employment_type &&
          pendingValue !== null &&
          pendingValue !== undefined && (
          <div
            data-testid="commission-pending-notice"
            className="mb-5 flex flex-col gap-3 rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="text-sm">
              <p className="font-semibold text-amber-700 dark:text-amber-300">
                Jutalék módosítás jóváhagyásra vár
              </p>
              <p className="text-xs text-muted-foreground">
                Jelenlegi: <b>{formatPct(currentValue)}</b> → új:{" "}
                <b>{formatPct(pendingValue)}</b>
                {employee.default_gross_commission_pending_set_by_name &&
                  ` · javasolta: ${employee.default_gross_commission_pending_set_by_name}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={rejectCommission}
                data-testid="commission-reject"
              >
                <X className="h-3.5 w-3.5" /> Elutasítás
              </Button>
              <Button
                size="sm"
                disabled={approving}
                onClick={approveCommission}
                data-testid="commission-approve"
              >
                {approving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}{" "}
                Jóváhagyás
              </Button>
            </div>
          </div>
        )}

        {employee.employment_type && !editing && (
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <Item label="Munkaviszony típusa">
              {labelOf(EMPLOYMENT_TYPE_OPTIONS, employee.employment_type)}
            </Item>
            <Item label="Munkaviszony kezdete">
              {formatDateHu(employee.employment_start_date)}
            </Item>
            <Item label="Próbaidő vége">
              {formatDateHu(employee.probation_end_date)}
            </Item>
            <Item label="Pozíció">
              {labelOf(POSITION_OPTIONS, employee.position)}
            </Item>
            <Item label="Munkáltató cég">
              {companyName || (employee.company_id ? "—" : "—")}
            </Item>
            <Item label="Telephely">{siteName || "—"}</Item>
            <Item label="Bruttó órabér">
              {employee.gross_hourly_rate != null
                ? `${formatHuf(employee.gross_hourly_rate)} Ft / óra`
                : "—"}
            </Item>
            <Item label="Bruttó havi bér">
              {employee.gross_monthly_salary != null
                ? `${formatHuf(employee.gross_monthly_salary)} Ft / hó`
                : "—"}
            </Item>
            <Item label="Bruttó órabér betűvel">
              {employee.gross_hourly_rate_words || "—"}
            </Item>
            <Item label="Bruttó havi bér betűvel">
              {employee.gross_monthly_salary_words || "—"}
            </Item>
            <Item label="Fix forgalom utáni jutalék">
              {currentValue != null ? (
                <span className="flex items-center gap-2">
                  {formatPct(currentValue)}
                  <button
                    type="button"
                    onClick={() => {
                      setProposeValue(String(currentValue));
                      setProposeOpen(true);
                    }}
                    data-testid="commission-propose"
                    className="rounded-md p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                    aria-label="Jutalék módosítás"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setProposeValue("");
                    setProposeOpen(true);
                  }}
                  data-testid="commission-propose"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  + Beállítás (jóváhagyásra)
                </button>
              )}
            </Item>
            <Item label="FEOR szám">{employee.feor_number || "—"}</Item>
            <Item label="Foglalkoztatás módja">
              {labelOf(EMPLOYMENT_MODE_OPTIONS, employee.employment_mode)}
            </Item>
            <Item label="Beosztás rendje">
              {labelOf(SCHEDULE_OPTIONS, employee.schedule_type)}
            </Item>
            <Item label="Heti munkaidő">
              {employee.weekly_hours
                ? `${employee.weekly_hours} óra / hét`
                : "—"}
            </Item>
          </dl>
        )}

        {editing && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FieldLabel label="Munkaviszony típusa">
              <Select
                value={form.employment_type || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, employment_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Pozíció">
              <Select
                value={form.position || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Munkaviszony kezdete">
              <DateInput
                value={form.employment_start_date || ""}
                onChange={(v) =>
                  setForm((f) => ({ ...f, employment_start_date: v }))
                }
              />
            </FieldLabel>
            <FieldLabel label="Próbaidő vége">
              <DateInput
                value={form.probation_end_date || ""}
                onChange={(v) =>
                  setForm((f) => ({ ...f, probation_end_date: v }))
                }
              />
            </FieldLabel>
            <FieldLabel label="Munkáltató cég">
              <Select
                value={form.company_id || ""}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, company_id: v, site_id: "" }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz céget…" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Telephely">
              <Select
                value={form.site_id || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, site_id: v }))}
                disabled={!form.company_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz telephelyet…" />
                </SelectTrigger>
                <SelectContent>
                  {visibleSites.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Bruttó órabér (Ft)">
              <NumericInput
                value={form.gross_hourly_rate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gross_hourly_rate: e.target.value }))
                }
              />
            </FieldLabel>
            <FieldLabel label="Bruttó havi bér (Ft)">
              <NumericInput
                value={form.gross_monthly_salary}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gross_monthly_salary: e.target.value }))
                }
              />
            </FieldLabel>
            <FieldLabel label="Bruttó órabér betűvel" wide>
              <Input
                value={form.gross_hourly_rate_words || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gross_hourly_rate_words: e.target.value }))
                }
                placeholder="pl. ezerötszáz forint"
              />
            </FieldLabel>
            <FieldLabel label="Bruttó havi bér betűvel" wide>
              <Input
                value={form.gross_monthly_salary_words || ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    gross_monthly_salary_words: e.target.value,
                  }))
                }
                placeholder="pl. háromszázezer forint"
              />
            </FieldLabel>
            <FieldLabel label="FEOR szám">
              <Input
                value={form.feor_number || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, feor_number: e.target.value }))
                }
                placeholder="pl. 5113"
                maxLength={20}
              />
            </FieldLabel>
            <FieldLabel label="Foglalkoztatás módja">
              <Select
                value={form.employment_mode || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, employment_mode: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Beosztás rendje">
              <Select
                value={form.schedule_type || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, schedule_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            <FieldLabel label="Heti munkaidő">
              <Select
                value={form.weekly_hours ? String(form.weekly_hours) : ""}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, weekly_hours: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKLY_HOURS_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={String(o.v)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldLabel>
            {err && (
              <div
                data-testid="employment-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive sm:col-span-2"
              >
                {err}
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={proposeOpen} onOpenChange={setProposeOpen}>
        <AlertDialogContent data-testid="commission-propose-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Fix forgalom utáni jutalék módosítása
            </AlertDialogTitle>
            <AlertDialogDescription>
              Az új érték <b>jóváhagyásra vár</b>, amíg egy jogosult kolléga
              ellenőrzi. A jelenleg érvényes százalék addig nem változik.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Új érték (%)
            </p>
            <Input
              type="number"
              data-testid="commission-propose-input"
              value={proposeValue}
              onChange={(e) => setProposeValue(e.target.value)}
              placeholder="pl. 10"
              min="0"
              max="100"
              step="0.1"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                submitCommissionPropose();
              }}
              data-testid="commission-propose-submit"
            >
              Javaslat beküldése
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TerminationDialog
        open={terminationOpen}
        onOpenChange={setTerminationOpen}
        employee={employee}
        onEmployeeChange={onEmployeeChange}
      />

      {employee.employment_history?.length > 0 && (
        <EmploymentHistoryList
          history={employee.employment_history}
          companies={companies}
          sites={sites}
          employeeId={employee._id}
        />
      )}
    </section>
  );
}

function FieldLabel({ label, children, wide }) {
  return (
    <label className={cn("block space-y-1.5", wide && "sm:col-span-2")}>
      <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function Item({ label, children, wide }) {
  return (
    <div className={cn(wide && "sm:col-span-2")}>
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function Mini({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <Icon className="h-3 w-3" strokeWidth={1.75} />
      <span className="text-foreground">{children}</span>
    </span>
  );
}

/* -------------------- Termination dialog -------------------- */

const LEAVE_SETTLEMENT_OPTIONS = [
  { v: "paid_out", label: "Kifizetésre kerül" },
  { v: "recorded_attendance", label: "Jelenlétiben kiírásra kerül" },
];

function TerminationDialog({ open, onOpenChange, employee, onEmployeeChange }) {
  const isFtPt =
    employee.employment_type === "full_time" ||
    employee.employment_type === "part_time";
  const inNotice = !!employee.current_termination;
  const [form, setForm] = useState({
    reason: "",
    leave_settlement: "",
    termination_date: "",
    termination_end_date: "",
  });
  const [letterFile, setLetterFile] = useState(null);
  const [exitFile, setExitFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setLetterFile(null);
    setExitFile(null);
    if (inNotice) {
      const ct = employee.current_termination || {};
      setForm({
        reason: ct.reason || "",
        leave_settlement: ct.leave_settlement || "",
        termination_date: ct.termination_date || "",
        termination_end_date: ct.termination_end_date || "",
      });
    } else {
      setForm({ reason: "", leave_settlement: "", termination_date: "", termination_end_date: "" });
    }
  }, [open, inNotice, employee.current_termination]);

  const startTermination = async () => {
    setErr("");
    if (!form.reason.trim()) return setErr("Felmondás oka kötelező.");
    if (!form.leave_settlement) return setErr("Szabadság elszámolás módja kötelező.");
    if (!form.termination_date) return setErr("Felmondás ideje kötelező.");
    if (isFtPt) {
      if (!form.termination_end_date)
        return setErr("Teljes/részmunkaidős esetén a felmondási idő vége kötelező.");
      if (!letterFile) return setErr("A felmondás PDF feltöltése kötelező.");
      if (!exitFile) return setErr("A kilépő papírok PDF feltöltése kötelező.");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("reason", form.reason);
      fd.append("leave_settlement", form.leave_settlement);
      fd.append("termination_date", form.termination_date);
      if (form.termination_end_date)
        fd.append("termination_end_date", form.termination_end_date);
      if (letterFile) fd.append("termination_letter", letterFile);
      if (exitFile) fd.append("exit_papers", exitFile);
      const { data } = await api.post(
        `/employees/${employee._id}/termination/start`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onEmployeeChange(data);
      toast.success("Felmondási idő elindítva.");
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const cancelTermination = async () => {
    if (!window.confirm("Visszavonod a folyamatban lévő felmondást?")) return;
    try {
      const { data } = await api.post(`/employees/${employee._id}/termination/cancel`);
      onEmployeeChange(data);
      toast.success("Felmondás visszavonva.");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült visszavonni.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="termination-dialog" className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{inNotice ? "Folyamatban lévő felmondás" : "Felmondás indítása"}</DialogTitle>
          <DialogDescription>
            {inNotice
              ? "A munkaviszony jelenleg felmondási időszakban van."
              : "A munkavállaló státusza 'Felmondási idő folyamatban' lesz."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <TermField label="Felmondás oka *">
            <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              rows={3} placeholder="A felmondás indoklása" disabled={inNotice} data-testid="termination-reason" />
          </TermField>
          <TermField label="Szabadság elszámolás módja *">
            <Select value={form.leave_settlement} onValueChange={(v) => setForm((f) => ({ ...f, leave_settlement: v }))} disabled={inNotice}>
              <SelectTrigger data-testid="termination-leave"><SelectValue placeholder="Válassz…" /></SelectTrigger>
              <SelectContent>
                {LEAVE_SETTLEMENT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </TermField>
          <TermField label="Felmondás ideje *">
            <DateInput
              value={form.termination_date}
              onChange={(v) => setForm((f) => ({ ...f, termination_date: v }))}
              disabled={inNotice}
              data-testid="termination-date"
            />
          </TermField>
          {isFtPt && (
            <TermField label="Felmondási idő vége *">
              <DateInput
                value={form.termination_end_date}
                onChange={(v) => setForm((f) => ({ ...f, termination_end_date: v }))}
                disabled={inNotice}
                data-testid="termination-end-date"
              />
            </TermField>
          )}
          {isFtPt && !inNotice && (
            <>
              <TermField label="Felmondás (PDF) *">
                <PdfPicker
                  testid="termination-letter-input"
                  file={letterFile}
                  onChange={setLetterFile}
                  placeholder="Felmondás PDF kiválasztása"
                />
              </TermField>
              <TermField label="Kilépő papírok (PDF) *">
                <PdfPicker
                  testid="exit-papers-input"
                  file={exitFile}
                  onChange={setExitFile}
                  placeholder="Kilépő papírok PDF kiválasztása"
                />
              </TermField>
            </>
          )}
          {isFtPt && inNotice && (
            <div className="grid grid-cols-1 gap-2 text-xs">
              <p className="text-muted-foreground">Feltöltött dokumentumok:</p>
              <TerminationFileLink
                label="Felmondás (PDF)"
                kind="termination_letter"
                termination={employee.current_termination}
                downloadPath={`/employees/${employee._id}/termination/file/termination_letter`}
              />
              <TerminationFileLink
                label="Kilépő papírok (PDF)"
                kind="exit_papers"
                termination={employee.current_termination}
                downloadPath={`/employees/${employee._id}/termination/file/exit_papers`}
              />
            </div>
          )}
          {err && (
            <div data-testid="termination-error" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {inNotice ? (
            <>
              <Button variant="outline" onClick={cancelTermination} data-testid="termination-cancel-action">Felmondás visszavonása</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Bezárás</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
              <Button onClick={startTermination} disabled={saving} data-testid="termination-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Felmondás indítása
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PdfPicker({ file, onChange, placeholder, testid }) {
  const inputId = `pdf-${testid}`;
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={inputId}
        className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-muted"
      >
        Tallózás
      </label>
      <input
        id={inputId}
        type="file"
        accept="application/pdf"
        className="hidden"
        data-testid={testid}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      <span className="truncate text-xs text-muted-foreground">
        {file ? file.name : placeholder}
      </span>
      {file && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function TerminationFileLink({ label, kind, termination, downloadPath }) {
  const has = termination?.[`${kind}_url`];
  if (!has) return <p className="text-muted-foreground/70">— {label}: nincs feltöltve</p>;
  return (
    <button
      type="button"
      onClick={() =>
        openProtectedFile(
          downloadPath,
          termination?.[`${kind}_content_type`],
          label
        )
      }
      data-testid={`termination-file-${kind}`}
      className="inline-flex items-center gap-1.5 text-left text-blue-600 hover:underline dark:text-blue-400"
    >
      <Paperclip className="h-3 w-3" />
      {label} · {termination?.[`${kind}_name`] || "fájl"}
    </button>
  );
}

function TermField({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/* -------------------- Notice banner -------------------- */

function NoticeBanner({ employee, onEmployeeChange }) {
  const [finalizing, setFinalizing] = useState(false);
  const ct = employee.current_termination || {};
  const isFtPt = employee.employment_type === "full_time" || employee.employment_type === "part_time";
  const leaveLabel = labelOf(LEAVE_SETTLEMENT_OPTIONS, ct.leave_settlement);

  const finalize = async () => {
    if (!window.confirm("Lezárod a felmondási időt? A munkaviszony 'Felmondott' státuszba kerül és az aktív adatok az előzményekbe archiválódnak.")) return;
    setFinalizing(true);
    try {
      const { data } = await api.post(`/employees/${employee._id}/termination/finalize`);
      onEmployeeChange(data);
      toast.success("Munkaviszony lezárva (Felmondott).");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült lezárni.");
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div data-testid="notice-period-banner"
      className="mb-5 flex flex-col gap-3 rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 font-semibold text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" /> Felmondási idő folyamatban
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Felmondás ideje: <b>{formatDateHu(ct.termination_date)}</b>
          {isFtPt && ct.termination_end_date && (<> · Felmondási idő vége: <b>{formatDateHu(ct.termination_end_date)}</b></>)}
          {" "} · Szabadság: <b>{leaveLabel}</b>
        </p>
        {ct.started_by_name && (
          <p className="mt-1 text-xs text-muted-foreground">
            Indította: <b className="text-foreground">{ct.started_by_name}</b>
            {ct.started_at && <> · {formatDateHu(ct.started_at)}</>}
          </p>
        )}
        {ct.reason && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">Oka: {ct.reason}</p>}
        {isFtPt && (ct.termination_letter_url || ct.exit_papers_url) && (
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            {ct.termination_letter_url && (
              <button
                type="button"
                onClick={() =>
                  openProtectedFile(
                    `/employees/${employee._id}/termination/file/termination_letter`,
                    ct.termination_letter_content_type,
                    "felmondás"
                  )
                }
                data-testid="notice-letter-link"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
              >
                <Paperclip className="h-3 w-3" /> Felmondás (PDF)
              </button>
            )}
            {ct.exit_papers_url && (
              <button
                type="button"
                onClick={() =>
                  openProtectedFile(
                    `/employees/${employee._id}/termination/file/exit_papers`,
                    ct.exit_papers_content_type,
                    "kilépő papírok"
                  )
                }
                data-testid="notice-exit-link"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
              >
                <Paperclip className="h-3 w-3" /> Kilépő papírok (PDF)
              </button>
            )}
          </div>
        )}
      </div>
      <Button size="sm" onClick={finalize} disabled={finalizing} data-testid="notice-finalize"
        className="bg-rose-600 text-white hover:bg-rose-700">
        {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        Felmondott státuszba
      </Button>
    </div>
  );
}

/* -------------------- Employment history list -------------------- */

const ALL_TYPES = [
  { v: "efo", label: "EFO" },
  { v: "student_coop", label: "Diákszövetkezet" },
  { v: "full_time", label: "Teljes munkaidős" },
  { v: "part_time", label: "Részmunkaidős" },
];

function EmploymentHistoryList({ history, companies, sites, employeeId }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="border-t border-border bg-muted/20 p-5">
      <div className="mb-4 flex items-center gap-1.5 text-muted-foreground">
        <History className="h-3.5 w-3.5" strokeWidth={2} />
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">Korábbi munkaviszonyok</p>
      </div>
      <ul className="space-y-3">
        {history.slice().reverse().map((h) => {
          const co = companies.find((c) => c._id === h.company_id)?.name || "—";
          const st = sites.find((s) => s._id === h.site_id)?.name || "—";
          const t = h.termination || {};
          return (
            <li key={h._id}>
              <button
                type="button"
                data-testid={`history-item-${h._id}`}
                onClick={() => setSelected(h)}
                className="w-full rounded-md border border-border bg-background p-3 text-left text-sm transition hover:border-foreground/20 hover:bg-foreground/[0.03] focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 font-medium">
                    {labelOf(ALL_TYPES, h.employment_type)}
                  </span>
                  <span className="text-muted-foreground">{co} · {st}</span>
                </div>
                <p className="mt-1.5 font-medium">{labelOf(POSITION_OPTIONS, h.position)}</p>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
                  <p>Kezdet: <b>{formatDateHu(h.employment_start_date)}</b></p>
                  <p>Lezárult: <b>{formatDateHu(t.termination_end_date || t.termination_date)}</b></p>
                  {t.started_by_name && (
                    <p className="sm:col-span-2">Megszüntette: <span className="text-foreground">{t.started_by_name}</span></p>
                  )}
                </dl>
              </button>
            </li>
          );
        })}
      </ul>

      <HistoryDetailDialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        entry={selected}
        companies={companies}
        sites={sites}
        employeeId={employeeId}
      />
    </div>
  );
}

function HistoryDetailDialog({ open, onOpenChange, entry, companies, sites, employeeId }) {
  if (!entry) return null;
  const t = entry.termination || {};
  const co = companies.find((c) => c._id === entry.company_id)?.name || "—";
  const st = sites.find((s) => s._id === entry.site_id)?.name || "—";
  const isFtPt = entry.employment_type === "full_time" || entry.employment_type === "part_time";

  const openFile = (kind, fallback) =>
    openProtectedFile(
      `/employees/${employeeId}/history/${entry._id}/file/${kind}`,
      t?.[`${kind}_content_type`],
      fallback
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="history-detail-dialog"
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>Korábbi munkaviszony adatai</DialogTitle>
          <DialogDescription>
            {labelOf(ALL_TYPES, entry.employment_type)} · {co} · {st}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <section>
            <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Munkaviszony
            </p>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              <Item label="Típus">{labelOf(ALL_TYPES, entry.employment_type)}</Item>
              <Item label="Pozíció">{labelOf(POSITION_OPTIONS, entry.position)}</Item>
              <Item label="Munkáltató cég">{co}</Item>
              <Item label="Telephely">{st}</Item>
              <Item label="Kezdet">{formatDateHu(entry.employment_start_date)}</Item>
              <Item label="Próbaidő vége">{formatDateHu(entry.probation_end_date)}</Item>
              <Item label="Foglalkoztatás módja">
                {labelOf(EMPLOYMENT_MODE_OPTIONS, entry.employment_mode)}
              </Item>
              <Item label="Beosztás rendje">
                {labelOf(SCHEDULE_OPTIONS, entry.schedule_type)}
              </Item>
              <Item label="Heti munkaidő">
                {entry.weekly_hours ? `${entry.weekly_hours} óra / hét` : "—"}
              </Item>
              <Item label="FEOR szám">{entry.feor_number || "—"}</Item>
              <Item label="Bruttó órabér">
                {entry.gross_hourly_rate != null
                  ? `${formatHuf(entry.gross_hourly_rate)} Ft / óra`
                  : "—"}
              </Item>
              <Item label="Bruttó havi bér">
                {entry.gross_monthly_salary != null
                  ? `${formatHuf(entry.gross_monthly_salary)} Ft / hó`
                  : "—"}
              </Item>
              <Item label="Bruttó órabér betűvel">
                {entry.gross_hourly_rate_words || "—"}
              </Item>
              <Item label="Bruttó havi bér betűvel">
                {entry.gross_monthly_salary_words || "—"}
              </Item>
              <Item label="Fix forgalom utáni jutalék">
                {entry.default_gross_commission != null
                  ? formatPct(entry.default_gross_commission)
                  : "—"}
              </Item>
            </dl>
          </section>

          <section className="rounded-md border border-border bg-muted/30 p-4">
            <p className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <UserCheck className="h-3 w-3" /> Felmondás
            </p>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              <Item label="Megszüntette" wide>
                {t.started_by_name ? (
                  <span>
                    <b>{t.started_by_name}</b>
                    {t.started_at && (
                      <span className="text-muted-foreground"> · {formatDateHu(t.started_at)}</span>
                    )}
                  </span>
                ) : (
                  "—"
                )}
              </Item>
              <Item label="Archiválta" wide>
                {entry.archived_by_name ? (
                  <span>
                    <b>{entry.archived_by_name}</b>
                    {entry.archived_at && (
                      <span className="text-muted-foreground"> · {formatDateHu(entry.archived_at)}</span>
                    )}
                  </span>
                ) : (
                  "—"
                )}
              </Item>
              <Item label="Felmondás ideje">{formatDateHu(t.termination_date)}</Item>
              <Item label="Felmondási idő vége">{formatDateHu(t.termination_end_date)}</Item>
              <Item label="Szabadság elszámolás">
                {labelOf(LEAVE_SETTLEMENT_OPTIONS, t.leave_settlement)}
              </Item>
              <Item label="Felmondás oka" wide>
                <span className="whitespace-pre-wrap">{t.reason || "—"}</span>
              </Item>
              {isFtPt && (
                <Item label="Dokumentumok" wide>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {t.termination_letter_url ? (
                      <button
                        type="button"
                        onClick={() => openFile("termination_letter", "felmondás")}
                        data-testid="history-letter-link"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                      >
                        <Paperclip className="h-3 w-3" /> Felmondás (PDF)
                      </button>
                    ) : (
                      <span className="text-muted-foreground">— Felmondás: nincs feltöltve</span>
                    )}
                    {t.exit_papers_url ? (
                      <button
                        type="button"
                        onClick={() => openFile("exit_papers", "kilépő papírok")}
                        data-testid="history-exit-link"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                      >
                        <Paperclip className="h-3 w-3" /> Kilépő papírok (PDF)
                      </button>
                    ) : (
                      <span className="text-muted-foreground">— Kilépő papírok: nincs feltöltve</span>
                    )}
                  </div>
                </Item>
              )}
            </dl>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Bezárás</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

